import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { useTranslation } from 'react-i18next';
import { Download, Upload, ArrowLeft, Database, AlertTriangle, Check, FileJson, Calendar, Users, BookOpen, Settings, Clock, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_EMAILS = ["julie@jcp-teaching.com", "test@example.com", "jc-p@outlook.de"];

const AdminBackupPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, loading: authLoading, token } = useAuth();
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [restoreStats, setRestoreStats] = useState(null);
  const [automaticBackups, setAutomaticBackups] = useState([]);
  const [loadingBackups, setLoadingBackups] = useState(true);
  const fileInputRef = useRef(null);

  const fetchAutomaticBackups = async () => {
    if (!token) return;
    setLoadingBackups(true);
    try {
      const response = await axios.get(`${API}/admin/backups/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAutomaticBackups(response.data.backups || []);
    } catch (error) {
      console.error('Error fetching backups:', error);
    } finally {
      setLoadingBackups(false);
    }
  };

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }
    
    if (user && !ADMIN_EMAILS.includes(user.email)) {
      toast.error('Admin access required');
      navigate('/');
    }

    if (user && token) {
      fetchAutomaticBackups();
    }
  }, [user, authLoading, navigate, token]);

  const handleDownloadAutomaticBackup = async (filename) => {
    try {
      const response = await axios.get(`${API}/admin/backups/download/${filename}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(t('Backup downloaded successfully'));
    } catch (error) {
      console.error('Error downloading backup:', error);
      toast.error(t('Failed to download backup'));
    }
  };

  const handleDownloadBackup = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API}/admin/backup`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Create and download JSON file
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const date = new Date().toISOString().split('T')[0];
      a.download = `jcp-backup-${date}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(t('Backup downloaded successfully'));
    } catch (error) {
      console.error('Error downloading backup:', error);
      toast.error(error.response?.data?.detail || t('Failed to download backup'));
    } finally {
      setDownloading(false);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.json')) {
      toast.error(t('Please select a JSON file'));
      return;
    }
    
    setUploading(true);
    setRestoreStats(null);
    
    try {
      const text = await file.text();
      const backupData = JSON.parse(text);
      
      // Validate backup structure
      if (!backupData.backup_version || !backupData.backup_date) {
        throw new Error(t('Invalid backup file format'));
      }
      
      const response = await axios.post(`${API}/admin/restore`, backupData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      setRestoreStats(response.data);
      toast.success(t('Restore completed successfully'));
    } catch (error) {
      console.error('Error restoring backup:', error);
      if (error instanceof SyntaxError) {
        toast.error(t('Invalid JSON file'));
      } else {
        toast.error(error.response?.data?.detail || error.message || t('Failed to restore backup'));
      }
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (authLoading || !user) {
    return (
      <div>
        <Navbar />
        <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto', paddingTop: '120px' }}>
          <p>{t('Loading...')}</p>
        </div>
      </div>
    );
  }

  if (!ADMIN_EMAILS.includes(user.email)) {
    return null;
  }

  return (
    <div>
      <Navbar />
      <div className="booking-page" style={{ paddingTop: '100px', minHeight: '100vh', background: '#fafafa' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '2rem' }}>
          {/* Header */}
          <div style={{ 
            background: 'white', 
            padding: '2rem', 
            borderRadius: '12px', 
            marginBottom: '2rem',
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
          }}>
            <button
              onClick={() => navigate('/admin')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#666',
                marginBottom: '1rem',
                padding: 0
              }}
            >
              <ArrowLeft size={18} />
              {t('Back to Admin')}
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <Database size={32} color="#7c6f5e" />
              <div>
                <h1 className="booking-title" style={{ marginBottom: '0.25rem', fontSize: '1.75rem' }}>
                  {t('Database Backup')}
                </h1>
                <p style={{ color: '#666', marginBottom: '0', fontSize: '0.95rem' }}>
                  {t('Download or restore your database')}
                </p>
              </div>
            </div>
          </div>

          {/* Download Section */}
          <div style={{
            background: 'white',
            padding: '2rem',
            borderRadius: '12px',
            marginBottom: '1.5rem',
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '1.5rem' }}>
              <div style={{
                background: 'linear-gradient(135deg, #6b8e6f 0%, #8aad8e 100%)',
                borderRadius: '10px',
                padding: '0.75rem',
                color: 'white'
              }}>
                <Download size={24} />
              </div>
              <div>
                <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '1.25rem', color: '#333' }}>
                  {t('Download Backup')}
                </h2>
                <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
                  {t('Download a complete backup of all your data as a JSON file')}
                </p>
              </div>
            </div>
            
            <div style={{ 
              background: '#f8f9fa', 
              padding: '1rem', 
              borderRadius: '8px',
              marginBottom: '1.5rem'
            }}>
              <p style={{ margin: '0 0 0.75rem 0', fontWeight: 500, color: '#333', fontSize: '0.9rem' }}>
                {t('Backup includes')}:
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#666', fontSize: '0.85rem' }}>
                  <Users size={14} /> {t('Users')}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#666', fontSize: '0.85rem' }}>
                  <BookOpen size={14} /> {t('Bookings')}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#666', fontSize: '0.85rem' }}>
                  <Calendar size={14} /> {t('Availability')}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#666', fontSize: '0.85rem' }}>
                  <Calendar size={14} /> {t('Weekend Courses')}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#666', fontSize: '0.85rem' }}>
                  <BookOpen size={14} /> {t('Theory Clinics')}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#666', fontSize: '0.85rem' }}>
                  <Settings size={14} /> {t('Settings')}
                </div>
              </div>
            </div>
            
            <button
              onClick={handleDownloadBackup}
              disabled={downloading}
              data-testid="download-backup-btn"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.75rem',
                width: '100%',
                padding: '1rem',
                background: downloading ? '#ccc' : 'linear-gradient(135deg, #6b8e6f 0%, #8aad8e 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: downloading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              <Download size={20} />
              {downloading ? t('Downloading...') : t('Download Backup')}
            </button>
          </div>

          {/* Upload/Restore Section */}
          <div style={{
            background: 'white',
            padding: '2rem',
            borderRadius: '12px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '1.5rem' }}>
              <div style={{
                background: 'linear-gradient(135deg, #5a8b9e 0%, #7aa5b5 100%)',
                borderRadius: '10px',
                padding: '0.75rem',
                color: 'white'
              }}>
                <Upload size={24} />
              </div>
              <div>
                <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '1.25rem', color: '#333' }}>
                  {t('Restore Backup')}
                </h2>
                <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
                  {t('Upload a backup file to restore your data')}
                </p>
              </div>
            </div>

            {/* Warning */}
            <div style={{ 
              background: '#fff8e6', 
              border: '1px solid #ffd966',
              padding: '1rem', 
              borderRadius: '8px',
              marginBottom: '1.5rem',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.75rem'
            }}>
              <AlertTriangle size={20} color="#e6a700" style={{ flexShrink: 0, marginTop: '2px' }} />
              <div style={{ fontSize: '0.85rem', color: '#8a6d00' }}>
                <strong>{t('Note')}:</strong> {t('Restore will only add missing data. Existing records will not be overwritten or deleted.')}
              </div>
            </div>
            
            <input
              type="file"
              accept=".json"
              onChange={handleFileSelect}
              ref={fileInputRef}
              style={{ display: 'none' }}
              id="backup-file-input"
            />
            
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="upload-backup-btn"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.75rem',
                width: '100%',
                padding: '1rem',
                background: uploading ? '#ccc' : 'linear-gradient(135deg, #5a8b9e 0%, #7aa5b5 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: uploading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              <Upload size={20} />
              {uploading ? t('Restoring...') : t('Select Backup File')}
            </button>

            {/* Restore Results */}
            {restoreStats && (
              <div style={{ 
                marginTop: '1.5rem',
                padding: '1rem',
                background: '#e8f5e9',
                borderRadius: '8px',
                border: '1px solid #a5d6a7'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                  <Check size={20} color="#4caf50" />
                  <strong style={{ color: '#2e7d32' }}>{t('Restore completed')}</strong>
                </div>
                <div style={{ fontSize: '0.85rem', color: '#333' }}>
                  <p style={{ margin: '0 0 0.5rem 0' }}>
                    <strong>{t('Backup date')}:</strong> {new Date(restoreStats.backup_date).toLocaleString('da-DK')}
                  </p>
                  <p style={{ margin: '0 0 0.25rem 0', fontWeight: 500 }}>{t('Restored records')}:</p>
                  <ul style={{ margin: '0', paddingLeft: '1.5rem' }}>
                    <li>{t('Users')}: {restoreStats.restored?.users || 0}</li>
                    <li>{t('Bookings')}: {restoreStats.restored?.bookings || 0}</li>
                    <li>{t('Availability')}: {restoreStats.restored?.availability || 0}</li>
                    <li>{t('Weekend Courses')}: {restoreStats.restored?.weekend_courses || 0}</li>
                    <li>{t('Theory Clinics')}: {restoreStats.restored?.theorie_clinics || 0}</li>
                    <li>{t('Admin Events')}: {restoreStats.restored?.admin_events || 0}</li>
                    <li>{t('Settings')}: {restoreStats.restored?.service_visibility ? t('Yes') : t('No')}</li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* Automatic Backups Section */}
          <div style={{
            background: 'white',
            padding: '2rem',
            borderRadius: '12px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                <div style={{
                  background: 'linear-gradient(135deg, #8b7c5e 0%, #a59578 100%)',
                  borderRadius: '10px',
                  padding: '0.75rem',
                  color: 'white'
                }}>
                  <Clock size={24} />
                </div>
                <div>
                  <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '1.25rem', color: '#333' }}>
                    {t('Automatic Backups')}
                  </h2>
                  <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
                    {t('Daily backups at 15:00 - kept for 7 days')}
                  </p>
                </div>
              </div>
              <button
                onClick={fetchAutomaticBackups}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  background: '#f5f5f5',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  color: '#666',
                  fontSize: '0.85rem'
                }}
              >
                <RefreshCw size={16} />
                {t('Refresh')}
              </button>
            </div>

            {loadingBackups ? (
              <p style={{ color: '#666', textAlign: 'center', padding: '2rem' }}>
                {t('Loading...')}
              </p>
            ) : automaticBackups.length === 0 ? (
              <div style={{
                background: '#f8f9fa',
                padding: '2rem',
                borderRadius: '8px',
                textAlign: 'center',
                color: '#666'
              }}>
                <Clock size={32} style={{ opacity: 0.5, marginBottom: '0.5rem' }} />
                <p style={{ margin: 0 }}>{t('No automatic backups yet')}</p>
                <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.85rem' }}>
                  {t('First backup will be created at 15:00')}
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {automaticBackups.map((backup, index) => (
                  <div
                    key={backup.filename}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '1rem',
                      background: index === 0 ? '#e8f5e9' : '#f8f9fa',
                      borderRadius: '8px',
                      border: index === 0 ? '1px solid #a5d6a7' : '1px solid #e0e0e0'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <FileJson size={24} color={index === 0 ? '#4CAF50' : '#666'} />
                      <div>
                        <div style={{ fontWeight: 500, color: '#333', fontSize: '0.95rem' }}>
                          {backup.backup_date ? new Date(backup.backup_date).toLocaleString('da-DK') : backup.filename}
                          {index === 0 && (
                            <span style={{
                              marginLeft: '0.5rem',
                              background: '#4CAF50',
                              color: 'white',
                              padding: '0.15rem 0.5rem',
                              borderRadius: '10px',
                              fontSize: '0.7rem',
                              fontWeight: 600
                            }}>
                              {t('Latest')}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '0.25rem' }}>
                          {backup.user_count} {t('Users')} • {backup.booking_count} {t('Bookings')} • {backup.size_kb} KB
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDownloadAutomaticBackup(backup.filename)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.5rem 1rem',
                        background: index === 0 ? '#4CAF50' : '#7c6f5e',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: 500
                      }}
                    >
                      <Download size={16} />
                      {t('Download')}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminBackupPage;
